// TALKING OVERLAY — the standard way any deity talks, over whatever screen is showing.
// No modal chrome: a floating close plus three stacked mode buttons under it.
//   Phone — voice only, back and forth; no figure, so the screen stays visible.
//   Chat  — a typed chat box with the deity's agent.
//   Video — the deity's figure plus live voice, always in SyQi's half-body box. SyQi (and any
//           deity with a SyQi-standard LIVE_ASSETS set) lip-syncs; the others show their moving
//           hall loop (HALL_CLIPS), or the full-body still, cropped head-to-waist as a placeholder.
// Every request carries what's on screen (live/screenContext.ts), so the deity knows
// the screen and tool the user is on and can act on it.
//
// Any screen opens it through openTalk(); <TalkHost/> (mounted once at the app root, so a call survives
// screen changes) renders it. Voice agents can hand the call to another deity (live/appActions.ts).
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Check, ClipboardList, MessageSquare, MicOff, Pencil, Phone, Send, Video, X } from 'lucide-react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';
import { askAgent } from '../live/askAgent';
import { onHandOff } from '../live/appActions';
import { useLiveVoice } from '../live/useLiveVoice';
import { AvatarPuppet } from './AvatarPuppet';
import { Butterfly } from './ButterflyIcon';
import { HALL_CLIPS } from './hallClips';
import { HallFigure } from './HallFigure';
import { LIVE_ASSETS } from './liveAssets';
import { fullBodyFor } from './roster';
import { shapeFullFor } from './shapeRoster';
import { makeStyles, useV10 } from './theme';
import { Medallion } from './ui';

// eslint-disable-next-line @typescript-eslint/no-require-imports
// SyQi-only fallback poster: first frame of her talking loop, keyed (same 720x900 frame as the puppet).
const SYQI_HALF = require('../../assets/v10/syqi/syqi_poster.png');

export type TalkMode = 'audio' | 'chat' | 'video';

// ---- open from anywhere ------------------------------------------------------
type TalkTarget = { deityId: string; displayName: string; mode: TalkMode; intro?: string } | null;
let talkTarget: TalkTarget = null;
/** The open overlay's current mode, so a hand-off keeps the user in phone / video / chat. */
let talkMode: TalkMode = 'video';
const talkListeners = new Set<(t: TalkTarget) => void>();
const emitTalk = (next: TalkTarget) => {
  talkTarget = next;
  talkListeners.forEach((fn) => fn(next));
};

/** Open a deity's talking overlay (seat id, or 'syqi'). Defaults to video, like SyQi's butterfly. */
export function openTalk(deityId: string, displayName: string, mode: TalkMode = 'video', intro?: string): void {
  emitTalk({ deityId, displayName, mode, intro });
}

// A voice agent handed the call over ("connect me to ATLAS"): the new deity takes the same overlay,
// same screen, same mode, and greets first knowing who handed over.
onHandOff(({ id, name, from }) => {
  const mode = talkMode === 'chat' ? 'audio' : talkMode;
  openTalk(
    id,
    name,
    mode,
    `${from} just handed this live call over to you, ${name}. Greet the user briefly in character as ${name} (one sentence) and ask what they need from you. Do not repeat the hand-off.`,
  );
});
// KYRS's Audit (server: src/lib/agents/audit-protocol.ts): a fresh KYRS chat that opens with "Run an audit.".
// The open KYRS overlay takes it directly; otherwise it waits here until the overlay opens.
export const AUDIT_START = 'Run an audit.';
let pendingAudit = false;
const auditListeners = new Set<() => void>();
export function startAudit(): void {
  if (auditListeners.size) auditListeners.forEach((fn) => fn());
  else {
    pendingAudit = true;
    openTalk('kyrs', 'KYRS', 'chat');
  }
}
export function closeTalk(): void {
  emitTalk(null);
}
/** Same deity already open closes it; anything else opens. */
export function toggleTalk(deityId: string, displayName: string): void {
  if (talkTarget?.deityId === deityId) closeTalk();
  else openTalk(deityId, displayName);
}

/** The one overlay instance behind openTalk(). Keeps the last deity mounted while it fades out. */
export function TalkHost() {
  const [target, setTarget] = useState<TalkTarget>(talkTarget);
  const [last, setLast] = useState<Exclude<TalkTarget, null> | null>(talkTarget);
  useEffect(() => {
    const fn = (t: TalkTarget) => {
      setTarget(t);
      if (t) setLast(t);
    };
    talkListeners.add(fn);
    return () => {
      talkListeners.delete(fn);
    };
  }, []);
  if (!last) return null;
  return (
    <TalkingOverlay
      key={last.deityId}
      deityId={last.deityId}
      displayName={last.displayName}
      initialMode={last.mode}
      intro={last.intro}
      open={!!target && target.deityId === last.deityId}
      onClose={closeTalk}
    />
  );
}


/** Close + phone / chat / video buttons stacked down the right edge (shared with the onboarding narrator). */
export function TalkModeButtons({ displayName, mode, onClose, onAudio, onChat, onVideo, disabled = false }: {
  displayName: string; mode: TalkMode; onClose: () => void; onAudio: () => void; onChat: () => void; onVideo: () => void; disabled?: boolean;
}) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const ic = (m: TalkMode) => (mode === m ? t.goldBright : t.ivory);
  const btn = (m: TalkMode, top: number, icon: React.ReactNode, onPress: () => void, label: string) => (
    <Pressable onPress={onPress} disabled={disabled} style={[styles.close, { top: insets.top + top }, mode === m && styles.modeOn]} hitSlop={6} accessibilityLabel={label}>
      {icon}
    </Pressable>
  );
  return (
    <>
      <Pressable onPress={onClose} disabled={disabled} style={[styles.close, { top: insets.top + 56 }]} hitSlop={10} accessibilityLabel={`Close ${displayName}`}>
        <X size={18} color={t.ivory} />
      </Pressable>
      {btn('audio', 100, <Phone size={17} color={ic('audio')} />, onAudio, `Talk by voice with ${displayName}`)}
      {btn('chat', 144, <MessageSquare size={17} color={ic('chat')} />, onChat, `Chat with ${displayName}`)}
      {btn('video', 188, <Video size={17} color={ic('video')} />, onVideo, `See ${displayName} on video`)}
    </>
  );
}

/** Fraction of a full-body hall figure (head to feet) that is head-to-waist. */
const WAIST_FRAC = 0.55;

/** The speaker's figure exactly as the video mode draws it: SyQi's half-body talk loop (poster + puppet on web),
 *  otherwise the deity's hall loop / still. `hallOnly` skips the live lip-sync puppet and plays hall clip 0
 *  (used by onboarding, which never opens a live session). */
export function SpeakerFigure({ deityId, audioTrack = null, speaking = false, hallOnly = false, keepPoster = false, scale = 1, children }: {
  deityId: string; audioTrack?: MediaStreamTrack | null; speaking?: boolean; hallOnly?: boolean; /** Never fade the poster out under the puppet (so a blank puppet frame can't leave the stage empty). */ keepPoster?: boolean;
  /** Fraction of SyQi's box (the sector deity on the compass stands at 3/4). */ scale?: number; children?: React.ReactNode;
}) {
  const { height: H } = useWindowDimensions();
  const [drawn, setDrawn] = useState(false);
  const isSyqi = deityId === 'syqi';
  const puppetOk = !hallOnly || isSyqi;
  // Only a SyQi-standard talk set (keyed half-body loop + poster) counts; Clio's old puppet set is obsolete.
  const std = LIVE_ASSETS[deityId]?.poster ? LIVE_ASSETS[deityId] : undefined;
  const hasPuppet = puppetOk && Platform.OS === 'web' && !!std;
  const poster = puppetOk ? std?.poster ?? (isSyqi ? SYQI_HALF : undefined) : undefined;
  const rawHall = !hasPuppet && !poster ? HALL_CLIPS[deityId] : undefined;
  const hall = React.useMemo(
    () => (rawHall && hallOnly ? { ...rawHall, webm: rawHall.webm.slice(0, 1), mov: rawHall.mov.slice(0, 1) } : rawHall),
    [rawHall, hallOnly],
  );
  const still = !hasPuppet && !poster && !hall ? fullBodyFor(deityId) ?? shapeFullFor(deityId) : undefined;
  // Native has no lip-sync puppet yet: play the keyed talk loop (idle, mouth closed) over the poster.
  const alphaMov = std?.alphaMov;
  const nativeLoop = React.useMemo(
    () => (Platform.OS !== 'web' && poster && alphaMov ? { aspect: 0.8, poster, webm: [], mov: [alphaMov] } : undefined),
    [poster, alphaMov],
  );
  // Every deity appears exactly like SyQi: a half-body in SyQi's box. Until a deity has its own SyQi-standard
  // set, its full-body hall loop (or still) is the placeholder, scaled so head-to-waist fills that box, centred
  // and clipped at the bottom (the legs fall off the edge).
  const figH = H * 0.62 * scale;
  const figW = figH * 0.8;
  const fullH = figH / WAIST_FRAC;
  const fullW = fullH * (hall?.aspect ?? still?.aspect ?? 0.45);
  return (
    <View style={{ width: figW, height: figH, overflow: hall || still ? 'hidden' : 'visible' }}>
      {poster && nativeLoop ? (
        <HallFigure set={nativeLoop} width={figW} height={figH} />
      ) : poster ? (
        <Image source={poster} resizeMode="contain" style={{ width: figW, height: figH, opacity: hasPuppet && drawn && !keepPoster ? 0 : 1 }} />
      ) : null}
      {hasPuppet ? (
        <AvatarPuppet deity={deityId} audioTrack={audioTrack} speaking={speaking} onFirstFrame={() => setDrawn(true)} left={0} top={0} width={figW} height={figH} />
      ) : null}
      {hall || still ? (
        <View style={{ position: 'absolute', top: 0, left: (figW - fullW) / 2, width: fullW, height: fullH }}>
          {hall ? <HallFigure set={hall} width={fullW} height={fullH} /> : null}
          {still ? <Image source={still.src} resizeMode="contain" style={{ width: fullW, height: fullH }} /> : null}
        </View>
      ) : null}
      {children}
    </View>
  );
}

// ---- the overlay -------------------------------------------------------------
type ChatLine = { role: 'user' | 'deity' | 'done'; text: string };

// Every chat is saved server-side (chat_conversations / chat_messages, one thread per deity).
// The overlay remembers each deity's thread and reloads it on open. SyQi shares SyqiTool's key,
// so the overlay and the SyQi screen show the same conversation.
const lastConvKey = (deityId: string) => (deityId === 'syqi' ? 'syqi.lastConversationId' : `deity.lastConversationId.${deityId}`);
const APP_CONTEXT = /^\[APP CONTEXT[\s\S]*?\[END APP CONTEXT\]\s*/;
const STEP_CONTEXT = /\s*\[Current step context:[\s\S]*$/;

type Thread = { id: string; title: string | null; updated_at: string };
// The server files threads under the canonical tool id (src/lib/agents/deity-config.ts aliases).
const THREAD_TOOL_ID: Record<string, string> = { atlas: 'telos', oracle: 'telos', astrs: 'astra', jnus: 'janus' };

/** This deity's saved conversations, newest first. */
async function listThreads(deityId: string): Promise<Thread[]> {
  const q = supabase.from('chat_conversations').select('id, title, updated_at').order('updated_at', { ascending: false }).limit(50);
  const { data } = await (deityId === 'syqi' ? q.is('tool_id', null) : q.eq('tool_id', THREAD_TOOL_ID[deityId] ?? deityId));
  return (data || []) as Thread[];
}

async function loadMessages(id: string): Promise<ChatLine[]> {
  const { data } = await supabase.from('chat_messages').select('role, content').eq('conversation_id', id).order('created_at', { ascending: true });
  return ((data || []) as { role: string; content: string }[])
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({ role: m.role === 'user' ? ('user' as const) : ('deity' as const), text: (m.content || '').replace(APP_CONTEXT, '').replace(STEP_CONTEXT, '') }));
}

/** The thread to show on open: the last one used here, else (another device) the deity's most recent. */
async function loadThread(deityId: string): Promise<{ id?: string; lines: ChatLine[] }> {
  let id: string | null = null;
  try {
    id = await AsyncStorage.getItem(lastConvKey(deityId));
  } catch {}
  if (!id) id = (await listThreads(deityId))[0]?.id ?? null;
  if (!id) return { lines: [] };
  return { id, lines: await loadMessages(id) };
}

// Titles are the first message, which usually starts with the screen preamble; show the date then.
const bareTitle = (th: Thread, name: string) => (th.title || '').replace(new RegExp(`^${name}:\\s*`, 'i'), '').trim();
function threadLabel(th: Thread, name: string): string {
  const when = new Date(th.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const title = bareTitle(th, name);
  return title && !title.startsWith('[APP CONTEXT') ? `${title}  ·  ${when}` : when;
}

export function TalkingOverlay({
  deityId,
  displayName,
  open,
  onClose,
  initialMode = 'video',
  intro,
}: {
  deityId: string;
  displayName: string;
  open: boolean;
  onClose: () => void;
  initialMode?: TalkMode;
  /** Hand-off context: the deity greets first. */
  intro?: string;
}) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const { height: H } = useWindowDimensions();
  const live = useLiveVoice({ toolId: deityId, intro });
  const liveActive = live.state === 'connecting' || live.state === 'connected' || live.state === 'reconnecting';

  const [mode, setModeState] = useState<TalkMode>(initialMode);
  const setMode = (m: TalkMode) => {
    talkMode = m;
    setModeState(m);
  };
  const [chat, setChat] = useState<ChatLine[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const chatConv = useRef<string | undefined>(undefined);
  const chatRef = useRef<ScrollView | null>(null);
  const liveRef = useRef<ScrollView | null>(null);
  const [history, setHistory] = useState<Thread[] | null>(null); // non-null = the past-conversations list is showing

  const toggleHistory = () => {
    if (history) return setHistory(null);
    setHistory([]);
    listThreads(deityId)
      .then(setHistory)
      .catch(() => setHistory(null));
  };
  const [renaming, setRenaming] = useState<{ id: string; text: string } | null>(null);
  const saveRename = () => {
    if (!renaming) return;
    const { id } = renaming;
    const title = renaming.text.trim();
    setRenaming(null);
    if (!title) return;
    setHistory((h) => h && h.map((th) => (th.id === id ? { ...th, title } : th)));
    void supabase.from('chat_conversations').update({ title }).eq('id', id).then(() => {});
  };
  const openThread = (id: string | undefined) => {
    setHistory(null);
    chatConv.current = id;
    setChat([]);
    void AsyncStorage[id ? 'setItem' : 'removeItem'](lastConvKey(deityId), id as string).catch(() => {});
    if (id) void loadMessages(id).then((lines) => chatConv.current === id && setChat(lines)).catch(() => {});
  };

  // Quick fade + scale; unmount the tree once fully hidden.
  const a = useRef(new Animated.Value(0)).current;
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      setMode(initialMode);
      if (initialMode !== 'chat') void live.start(); // opening is the user gesture: the call starts already "pressed"
      Animated.timing(a, { toValue: 1, duration: 240, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    } else {
      live.end();
      chatConv.current = undefined;
      setChat([]);
      setDraft('');
      Animated.timing(a, { toValue: 0, duration: 180, easing: Easing.in(Easing.cubic), useNativeDriver: true }).start(({ finished }) => {
        if (finished) setMounted(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, a]);

  // Reload this deity's saved conversation whenever it opens (or a hand-off switches deity).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    chatConv.current = undefined;
    setChat([]);
    setHistory(null);
    if (pendingAudit && deityId === 'kyrs') {
      pendingAudit = false;
      beginAudit();
      return;
    }
    loadThread(deityId)
      .then((r) => {
        if (cancelled) return;
        chatConv.current = r.id;
        setChat((p) => (p.length ? [...r.lines, ...p] : r.lines));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, deityId]);

  // hooks must run before the early return (React needs the same hook order every render)
  const goVoice = (next: 'audio' | 'video') => {
    setMode(next);
    if (!liveActive) void live.start(); // phone <-> video keeps the same call going
  };
  const goChat = () => {
    live.end();
    setMode('chat');
  };
  const send = async (said?: string) => {
    const text = (said ?? draft).trim();
    if (!text || sending) return;
    if (said === undefined) setDraft('');
    setChat((p) => [...p, { role: 'user', text }]);
    setSending(true);
    try {
      const r = await askAgent(deityId, text, chatConv.current);
      if (r.conversationId) {
        chatConv.current = r.conversationId;
        void AsyncStorage.setItem(lastConvKey(deityId), r.conversationId).catch(() => {});
      }
      setChat((p) => [
        ...p,
        { role: 'deity', text: r.text || 'Done.' },
        ...r.applied.map((s) => ({ role: 'done' as const, text: s })),
      ]);
    } catch (e) {
      setChat((p) => [...p, { role: 'deity', text: `That request failed (${e instanceof Error ? e.message : 'unknown error'}). Nothing was changed. Try again?` }]);
    } finally {
      setSending(false);
    }
  };

  // An audit always starts its own conversation, in chat.
  const beginAudit = () => {
    live.end();
    setMode('chat');
    openThread(undefined);
    void send(AUDIT_START);
  };
  const beginAuditRef = useRef(beginAudit);
  beginAuditRef.current = beginAudit;
  useEffect(() => {
    if (!open || deityId !== 'kyrs') return;
    const fn = () => beginAuditRef.current();
    auditListeners.add(fn);
    return () => {
      auditListeners.delete(fn);
    };
  }, [open, deityId]);

  if (!mounted) return null;

  const isSyqi = deityId === 'syqi';
  const status = live.state === 'connecting' ? 'Connecting…' : live.state === 'reconnecting' ? 'Reconnecting…' : live.deitySpeaking ? 'Speaking' : live.muted ? 'Muted' : 'Listening';

  const badge = isSyqi ? (
    <View style={styles.syqiBadge}>
      <Butterfly size={20} color={t.goldBright} />
    </View>
  ) : (
    <Medallion id={deityId} size={40} />
  );

  return (
    <Animated.View pointerEvents="box-none" style={[StyleSheet.absoluteFill, styles.root, { opacity: a }]}>
      <TalkModeButtons displayName={displayName} mode={mode} onClose={onClose} onAudio={() => goVoice('audio')} onChat={goChat} onVideo={() => goVoice('video')} />

      <Animated.View
        pointerEvents="box-none"
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, alignItems: 'center', transform: [{ scale: a.interpolate({ inputRange: [0, 1], outputRange: [0.94, 1] }) }] }}
      >
        {mode === 'chat' ? (
          <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={[styles.chatBox, { maxHeight: H * 0.6, marginBottom: insets.bottom + 12 }]}>
            <Pressable onPress={toggleHistory} style={styles.chatHead} accessibilityLabel={`${displayName} past conversations`}>
              {badge}
              <Text style={styles.chatName}>{displayName}</Text>
              <Text style={styles.historyHint}>{history ? 'Back to chat' : 'Past chats'}</Text>
            </Pressable>
            {deityId === 'kyrs' && !history ? (
              <Pressable onPress={beginAudit} disabled={sending} style={styles.auditBtn} accessibilityLabel="Run an audit">
                <ClipboardList size={15} color={t.onGold} />
                <Text style={styles.auditText}>Run an audit</Text>
              </Pressable>
            ) : null}
            {history ? (
              <ScrollView style={{ flexGrow: 0, minHeight: 60 }} contentContainerStyle={{ gap: 6, paddingVertical: 6 }} showsVerticalScrollIndicator={false}>
                <Pressable onPress={() => openThread(undefined)} style={[styles.bubble, styles.mine]}>
                  <Text style={[styles.bubbleText, { color: t.onGold }]}>+ New conversation</Text>
                </Pressable>
                {history.map((th) =>
                  renaming?.id === th.id ? (
                    <View key={th.id} style={styles.threadRow}>
                      <TextInput
                        value={renaming.text}
                        onChangeText={(text) => setRenaming({ id: th.id, text })}
                        onSubmitEditing={saveRename}
                        autoFocus
                        placeholder="Chat name"
                        placeholderTextColor="#6b7280"
                        style={[styles.input, { height: 36 }]}
                      />
                      <Pressable onPress={saveRename} style={[styles.btn, styles.btnOn]} accessibilityLabel="Save name">
                        <Check size={16} color={t.onGold} />
                      </Pressable>
                    </View>
                  ) : (
                    <View key={th.id} style={styles.threadRow}>
                      <Pressable onPress={() => openThread(th.id)} style={[styles.bubble, styles.theirs, th.id === chatConv.current && { borderColor: t.gold }]}>
                        <Text style={styles.bubbleText}>{threadLabel(th, displayName)}</Text>
                      </Pressable>
                      <Pressable onPress={() => setRenaming({ id: th.id, text: bareTitle(th, displayName).startsWith('[APP CONTEXT') ? '' : bareTitle(th, displayName) })} hitSlop={8} accessibilityLabel="Rename chat">
                        <Pencil size={15} color={t.goldBright} />
                      </Pressable>
                    </View>
                  ),
                )}
                {history.length === 0 ? <Text style={styles.empty}>No saved conversations yet.</Text> : null}
              </ScrollView>
            ) : (
            <ScrollView
              ref={chatRef}
              style={{ flexGrow: 0, minHeight: 60 }}
              contentContainerStyle={{ gap: 6, paddingVertical: 6 }}
              onContentSizeChange={() => chatRef.current?.scrollToEnd({ animated: true })}
              showsVerticalScrollIndicator={false}
            >
              {chat.length === 0 ? <Text style={styles.empty}>{`Ask ${displayName} about this screen.`}</Text> : null}
              {chat.map((l, i) =>
                l.role === 'done' ? (
                  <Text key={i} style={styles.done}>
                    ✓ {l.text}
                  </Text>
                ) : (
                  <View key={i} style={[styles.bubble, l.role === 'user' ? styles.mine : styles.theirs]}>
                    <Text style={[styles.bubbleText, l.role === 'user' && { color: t.onGold }]}>{l.text.replace(/\*\*|__/g, '')}</Text>
                  </View>
                ),
              )}
              {sending ? (
                <View style={[styles.bubble, styles.theirs]}>
                  <Text style={styles.bubbleText}>…</Text>
                </View>
              ) : null}
            </ScrollView>
            )}
            <View style={styles.inputRow}>
              <TextInput
                value={draft}
                onChangeText={setDraft}
                onSubmitEditing={() => void send()}
                returnKeyType="send"
                placeholder={`Message ${displayName}`}
                placeholderTextColor="#6b7280"
                style={styles.input}
              />
              <Pressable onPress={() => void send()} style={[styles.btn, styles.btnOn]} accessibilityLabel="Send">
                <Send size={17} color={t.onGold} />
              </Pressable>
            </View>
          </KeyboardAvoidingView>
        ) : null}

        {mode !== 'chat' && live.transcript.length ? (
          // Live transcript of the call, so a voice conversation (an audit above all) can be read as it goes.
          <View style={[styles.chatBox, { maxHeight: H * (mode === 'video' ? 0.22 : 0.5), marginBottom: 8 }]}>
            <ScrollView
              ref={liveRef}
              style={{ flexGrow: 0 }}
              contentContainerStyle={{ gap: 6 }}
              onContentSizeChange={() => liveRef.current?.scrollToEnd({ animated: true })}
              showsVerticalScrollIndicator={false}
            >
              {live.transcript.map((l, i) => (
                <View key={i} style={[styles.bubble, l.role === 'user' ? styles.mine : styles.theirs]}>
                  <Text style={[styles.bubbleText, l.role === 'user' && { color: t.onGold }]}>{l.text}</Text>
                </View>
              ))}
            </ScrollView>
          </View>
        ) : null}

        {mode === 'video' ? (
          <>
            {live.error ? <Text style={styles.err}>{live.error}</Text> : null}
            <SpeakerFigure deityId={deityId} audioTrack={live.puppetTrack} speaking={live.deitySpeaking}>
              {liveActive ? <Pressable onPress={live.interrupt} accessibilityLabel={`Interrupt ${displayName}`} style={StyleSheet.absoluteFill} /> : null}
            </SpeakerFigure>
            {liveActive ? (
              <View style={[styles.controls, { bottom: insets.bottom + 12 }]}>
                <Text style={styles.status}>{status}</Text>
                <Pressable onPress={live.toggleMute} style={[styles.btn, live.muted && styles.btnOn]} accessibilityLabel={live.muted ? 'Unmute' : 'Mute'}>
                  <MicOff size={17} color={live.muted ? t.onGold : t.goldBright} />
                </Pressable>
              </View>
            ) : null}
          </>
        ) : null}

        {mode === 'audio' ? (
          <View style={[styles.callBar, { marginBottom: insets.bottom + 12 }]}>
            <Pressable onPress={liveActive ? live.interrupt : undefined} accessibilityLabel={`Interrupt ${displayName}`} style={[styles.callBadge, live.deitySpeaking && styles.callBadgeOn]}>
              {badge}
            </Pressable>
            <View style={{ flex: 1 }}>
              <Text style={styles.chatName}>{displayName}</Text>
              <Text style={[styles.status, live.error && { color: t.lapsed }]} numberOfLines={2}>
                {live.error ?? (liveActive ? status : 'Call ended')}
              </Text>
            </View>
            {liveActive ? (
              <Pressable onPress={live.toggleMute} style={[styles.btn, live.muted && styles.btnOn]} accessibilityLabel={live.muted ? 'Unmute' : 'Mute'}>
                <MicOff size={17} color={live.muted ? t.onGold : t.goldBright} />
              </Pressable>
            ) : (
              <Pressable onPress={() => void live.start()} style={[styles.btn, styles.btnOn]} accessibilityLabel={`Call ${displayName}`}>
                <Phone size={17} color={t.onGold} />
              </Pressable>
            )}
          </View>
        ) : null}
      </Animated.View>
    </Animated.View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { zIndex: 25 },
    close: { position: 'absolute', right: 14, width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.8)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)', zIndex: 2 },
    modeOn: { backgroundColor: 'rgba(214,180,106,0.28)', borderColor: t.goldBright, borderWidth: 2 },
    chatBox: { alignSelf: 'stretch', marginHorizontal: 12, padding: 12, gap: 8, borderRadius: 22, backgroundColor: 'rgba(9,14,32,0.94)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.45)' },
    chatHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    chatName: { color: t.ivory, fontSize: 15, fontWeight: '800', letterSpacing: 1.4, textTransform: 'uppercase' },
    threadRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    auditBtn: { alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, backgroundColor: t.gold },
    auditText: { color: t.onGold, fontSize: 13, fontWeight: '800' },
    historyHint: { marginLeft: 'auto', color: t.goldBright, fontSize: 12, fontWeight: '700' },
    empty: { color: t.ivoryFaint, fontStyle: 'italic', fontSize: 13, paddingVertical: 8 },
    done: { color: t.goldBright, fontSize: 12, fontStyle: 'italic', paddingHorizontal: 4 },
    inputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    input: { flex: 1, height: 44, borderRadius: 22, paddingHorizontal: 16, backgroundColor: '#ffffff', borderWidth: 1.5, borderColor: t.gold, color: '#111827', fontSize: 15 },
    bubble: { maxWidth: '88%', borderRadius: 16, paddingHorizontal: 12, paddingVertical: 7, borderWidth: 1 },
    theirs: { alignSelf: 'flex-start', backgroundColor: 'rgba(16,24,48,0.92)', borderColor: 'rgba(214,180,106,0.35)' },
    mine: { alignSelf: 'flex-end', backgroundColor: t.gold, borderColor: t.gold },
    bubbleText: { color: '#f3ead2', fontSize: 14, lineHeight: 19 },
    err: { color: t.lapsed, fontSize: 12, paddingHorizontal: 16 },
    controls: { position: 'absolute', alignSelf: 'center', flexDirection: 'row', alignItems: 'center', gap: 8 },
    callBar: { alignSelf: 'stretch', marginHorizontal: 12, flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 12, paddingVertical: 10, borderRadius: 999, backgroundColor: 'rgba(9,14,32,0.94)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.45)' },
    callBadge: { borderRadius: 26, padding: 2, borderWidth: 2, borderColor: 'transparent' },
    callBadgeOn: { borderColor: t.goldBright },
    syqiBadge: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: t.gold, backgroundColor: 'rgba(16,24,48,0.92)' },
    btn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: t.gold, backgroundColor: 'rgba(9,14,32,0.85)' },
    btnOn: { backgroundColor: t.gold },
    status: { color: t.goldBright, fontSize: 11, fontWeight: '800', letterSpacing: 1 },
  }),
);
